import {PureComponent, ComponentType} from "react";
import {AnimationSettingsProp} from "../../../store/settings/types";
import React from "react";
import {TopPanelProps} from "./TopPanelProps";
import {TweenLite, TimelineLite, Animation} from "gsap";
import styles from "./top-panel.module.css";
import icons from "../../styles/icons.module.css";
import {HostElementRefProp} from "../HostElementRefProp";
import {TopPanel} from "./TopPanel";
import playSound from "../../sounds/playSound";

type AnimatedTopPanelState = {
    readonly coins: number;
    readonly coinsDelta: number;
}

export const animateTopPanel = (Tile: ComponentType<HostElementRefProp & any>) => {
    return class extends PureComponent<TopPanelProps & AnimationSettingsProp, AnimatedTopPanelState> {
        private readonly hostElement: React.RefObject<HTMLDivElement>;
        private readonly activeTweens: Animation[] = [];

        constructor(props: TopPanelProps & AnimationSettingsProp, context: any) {
            super(props, context);

            this.hostElement = React.createRef();

            this.state = {
                coins: props.coins,
                coinsDelta: 0
            };
        }

        componentDidMount(): void {
            const {animation} = this.props;

            if (!this.hostElement.current || !animation.enabled || !this.props.animate)
                return;

            const tween = TweenLite.from(this.hostElement.current, 0.33, {
                y: -130,
                delay: this.props.delay || 0.6,
                onStart: () => this.swoosh()
            }).timeScale(animation.timeScale);

            this.activeTweens.push(tween);
        }

        componentDidUpdate(prevProps: Readonly<TopPanelProps & AnimationSettingsProp>, prevState: Readonly<AnimatedTopPanelState>, snapshot?: any): void {
            const {animation} = this.props;

            if (!this.hostElement.current || !animation.enabled)
                return;

            if (prevProps.coins !== this.props.coins) {
                const coins = this.hostElement.current.parentElement!.getElementsByClassName(styles.coins_value);
                const coinsTooltip = this.hostElement.current.parentElement!.getElementsByClassName(styles.coins_tooltip);
                if (!coinsTooltip.length)
                    return;

                const timeline = new TimelineLite()
                    .set(coinsTooltip, {
                        zIndex: 100,
                        opacity: 1,
                        scale: 1,
                        y: 0,
                    })
                    .from(coinsTooltip, 0.5, {
                        opacity: 0,
                        scale: 0,
                    }, "first")
                    .to(coinsTooltip, 1.0, {
                        y: 100,
                        opacity: 0,
                    }, "second")
                    .set(coinsTooltip, {
                        zIndex: -1,
                    });


                if (coins.length) {
                    const coinsCounter = { coins: prevProps.coins };
                    timeline.fromTo(coinsCounter, 1, {
                        coins: prevProps.coins,
                    }, {
                        coins: this.props.coins,
                        roundProps: "coins",
                        onUpdate: () => {
                            if (!coins || !coins[0])
                                return;

                            coins[0].innerHTML = coinsCounter.coins + "";
                        }
                    }, "first");
                }

                timeline.timeScale(animation.timeScale);

                this.activeTweens.push(timeline);
            }

            if (this.props.score !== prevProps.score) {
                const score = this.hostElement.current.parentElement!.getElementsByClassName(styles.score_value);
                const scoreIcon = this.hostElement.current.parentElement!.getElementsByClassName(styles.score_icon);
                if (!score.length || !scoreIcon.length)
                    return;

                const scoreCounter = { score: prevProps.score };
                const tween = TweenLite.fromTo(scoreCounter, 1, {
                    score: prevProps.score,
                }, {
                    score: this.props.score,
                    roundProps:"score",
                    delay: 2.0,
                    onUpdate: () => {
                        if (!score || !score[0])
                            return;

                        score[0].innerHTML = scoreCounter.score + "";
                    }
                });

                this.activeTweens.push(tween);
            }
        }

        componentWillUnmount(): void {
            for (let animation of this.activeTweens) {
                animation.kill();
            }
        }

        render() {
            const sign = this.state.coinsDelta > 0 ? '+' : '';
            const coinsTooltip = (this.state.coinsDelta !== 0)
                ? (
                    <div className={styles.coins_tooltip}>
                        <div className={styles.coins_tooltip_value}>
                            <i className={`${icons.icon} ${icons.coins}`}/>{sign}{this.state.coinsDelta}
                        </div>
                        <div className={styles.coins_tooltip_shadow}>
                            <i className={`${icons.icon} ${icons.coins}`}/>{sign}{this.state.coinsDelta}
                        </div>
                        <div className={styles.coins_tooltip_shadow}>
                            <i className={`${icons.icon} ${icons.coins}`}/>{sign}{this.state.coinsDelta}
                        </div>
                    </div>
                )
                : undefined;

            return (
                <>
                    <TopPanel {...this.props} hostElementRef={this.hostElement}/>
                    {coinsTooltip}
                </>
            );
        }

        private swoosh() {
            playSound("panel-swoosh", 0.2);
        }

        static getDerivedStateFromProps(props: Readonly<TopPanelProps & AnimationSettingsProp>, state: Readonly<AnimatedTopPanelState>): AnimatedTopPanelState | null {
            if (props.coins !== state.coins) {
                return {
                    coins: props.coins,
                    coinsDelta: props.coins - state.coins
                };
            }

            return null;
        }
    };
};